
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Check, X } from 'lucide-react';
import LaunchButton from './LaunchButton';

interface PricingFeature {
  name: string;
  included: boolean;
}

interface PricingCardProps {
  title: string;
  description: string;
  price: string;
  originalPrice?: string;
  priceDetail?: string;
  features: PricingFeature[];
  highlight?: boolean;
  buttonText: string;
  badge?: string;
  onButtonClick: () => void;
  isIndia?: boolean;
  discountPercentage?: number;
  computeCredits?: number;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  description,
  price,
  originalPrice,
  priceDetail,
  features,
  highlight = false,
  buttonText,
  badge = "Launch Special",
  onButtonClick,
  isIndia = true,
  discountPercentage,
  computeCredits,
}) => {
  // Format compute credits to display in millions when appropriate
  const formatComputeCredits = (credits?: number) => {
    if (!credits) return '';
    
    if (credits >= 1000000) {
      const millions = credits / 1000000;
      return `${millions} million compute credits`;
    }
    
    return `${credits.toLocaleString()} compute credits`;
  };

  return (
    <Card 
      className={`w-full max-w-sm flex flex-col transition-all duration-200 hover:translate-y-[-4px] 
      ${highlight ? 'border-krix-purple shadow-lg' : 'shadow-md hover:shadow-lg'}`}
    >
      {(highlight || discountPercentage) && (
        <div className={`w-full rounded-t-lg py-1.5 px-3 text-center text-sm font-medium 
        ${highlight ? 'bg-krix-purple text-white' : 'bg-green-100 text-green-800'}`}>
          {highlight ? badge : `Save ${discountPercentage}%`}
        </div>
      )}
      
      <CardHeader className="flex flex-col items-start pt-4">
        <CardTitle className={`text-xl font-bold ${highlight ? 'text-krix-purple' : ''}`}>{title}</CardTitle>
        <CardDescription className="text-sm text-muted-foreground">{description}</CardDescription>
        
        <div className="mt-5 mb-1 w-full">
          {originalPrice && (
            <div className="flex items-center justify-start mb-1">
              <span className="text-sm font-medium text-muted-foreground line-through">
                {originalPrice}
              </span>
              {discountPercentage && (
                <span className="ml-2 text-xs font-medium text-green-600 bg-green-100 px-1.5 py-0.5 rounded-full">
                  -{discountPercentage}%
                </span>
              )}
            </div>
          )}
          
          <div className="flex items-baseline">
            <span className={`text-3xl font-extrabold tracking-tight ${highlight ? 'text-krix-purple' : ''}`}>
              {price}
            </span>
            {priceDetail && (
              <span className="ml-1 text-sm text-muted-foreground">{priceDetail}</span>
            )}
          </div>
        </div>
        
        {computeCredits && (
          <div className="mt-2 text-sm font-medium px-3 py-1 bg-muted/50 rounded-full w-full text-center">
            {formatComputeCredits(computeCredits)}
          </div>
        )}
      </CardHeader>
      
      <CardContent className="flex-grow pt-4">
        <ul className="space-y-3">
          {features.map((feature, index) => (
            <li key={index} className="flex items-start">
              {feature.included ? (
                <Check className="h-5 w-5 mr-2 flex-shrink-0 text-krix-purple" />
              ) : (
                <X className="h-5 w-5 mr-2 flex-shrink-0 text-muted-foreground/30" />
              )}
              <span className={feature.included ? 'text-foreground' : 'text-muted-foreground/50'}>
                {feature.name}
              </span>
            </li>
          ))}
        </ul>
      </CardContent>
      
      <CardFooter className="pt-2">
        <LaunchButton 
          variant={highlight ? 'default' : 'outline'} 
          className={highlight 
            ? "w-full bg-krix-purple hover:bg-krix-purple/90 text-white" 
            : "w-full hover:bg-muted/50"} 
          onClick={onButtonClick}
        >
          {buttonText}
        </LaunchButton>
      </CardFooter>
    </Card>
  );
};

export default PricingCard;
